import { useCallback, useEffect, useRef, useState } from "react";
import { fetchAssistantHistory, sendAssistantChat } from "@/api/assistantHistory";
import { dashFetch, getActiveSite } from "@/api/client";
import { connectDashSocket } from "@/socket/dashSocket";
import { BRAND_ASSISTANT } from "@/brand";
import { useOptionalDashboard } from "@/context/DashboardContext";
import type { DashboardTab } from "@/types/dashboard";
import PageContentSearchModal, { pageContentSearchLabel } from "@/features/pages/PageContentSearchModal";
import type {
  AssistantMessage,
  AssistantPendingMutation,
  AssistantTableSpec,
  AssistantThinkingStep
} from "@/types/account";
import type { PageContentSearchSpec } from "@/types/pageContentSearch";
import type { AssistantMutationConfirmation } from "./types";
import AssistantMutationPrompt from "./AssistantMutationPrompt";
import { useOptionalAssistantTasks } from "./AssistantTaskContext";
import type { AssistantTask } from "./assistantTaskTypes";
import { runAssistantActionsFromChat } from "./AssistantBridge";
import { hydrateAssistantMessage } from "./assistantTables";
import "@/styles/assistant.css";

function escapeHtml(s: string): string {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function linkify(text: string): string {
  const esc = escapeHtml(text);
  return esc.replace(
    /(https?:\/\/[^\s<]+)/g,
    '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>'
  );
}

function newMessageId(): string {
  return `msg_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

/** Pending Confirm/Cancel only apply to the latest assistant turn. */
function clearStalePendingMutations(messages: AssistantMessage[]): AssistantMessage[] {
  return messages.map((m) => {
    if (m.role !== "assistant" || !m.pendingMutations?.length) return m;
    return {
      ...m,
      pendingMutations: m.pendingMutations.map((pm) =>
        pm.status === "pending" || pm.status === "running"
          ? { ...pm, status: "declined" as const }
          : pm
      )
    };
  });
}

function mergeThinkingStep(steps: AssistantThinkingStep[] | undefined, step: AssistantThinkingStep): AssistantThinkingStep[] {
  const list = Array.isArray(steps) ? [...steps] : [];
  const idx = list.findIndex((s) => s.id === step.id);
  if (idx >= 0) list[idx] = step;
  else list.push(step);
  return list;
}

function tablesFromActions(actions: unknown[]): AssistantTableSpec[] {
  return actions
    .filter((a) => a && typeof a === "object" && (a as { type?: string }).type === "show_table")
    .map((a) => {
      const t = a as AssistantTableSpec & { type?: string };
      return {
        tableId: t.tableId,
        title: t.title,
        subtitle: t.subtitle,
        columns: t.columns,
        rows: t.rows
      };
    });
}

function contentSearchesFromActions(actions: unknown[]): PageContentSearchSpec[] {
  return actions
    .filter(
      (a) => a && typeof a === "object" && (a as { type?: string }).type === "show_page_content_search"
    )
    .map((a) => {
      const row = a as { searchId?: string; payload?: PageContentSearchSpec["payload"] };
      return {
        searchId: row.searchId,
        payload: row.payload as PageContentSearchSpec["payload"]
      };
    })
    .filter((s) => s.payload && Array.isArray(s.payload.results));
}

function currentThinkingStep(steps: AssistantThinkingStep[] | undefined): AssistantThinkingStep | null {
  if (!steps?.length) return null;
  const active = steps.filter((s) => s.status === "active");
  if (active.length) return active[active.length - 1] ?? null;
  return steps[steps.length - 1] ?? null;
}

function AssistantStreamingBubble({
  message,
  linkify: linkifyFn
}: {
  message: AssistantMessage;
  linkify: (text: string) => string;
}) {
  const streamed = hydrateAssistantMessage(message.content, undefined, { hidePartial: true });
  const hasAnswer = Boolean(streamed.content.trim());
  if (hasAnswer) {
    return (
      <span
        className="creaAsstStreamText"
        dangerouslySetInnerHTML={{ __html: linkifyFn(streamed.content) }}
      />
    );
  }
  const step = currentThinkingStep(message.thinkingSteps);
  if (step) return <AssistantThinkingCurrent step={step} />;
  return <span className="muted">Thinking…</span>;
}

function AssistantThinkingCurrent({ step }: { step: AssistantThinkingStep }) {
  return (
    <div className="creaAsstThinking creaAsstThinking--live" aria-live="polite">
      <div className="creaAsstThinkingStep active">
        <span className="creaAsstThinkingDot" aria-hidden="true" />
        <span>{step.label}</span>
      </div>
    </div>
  );
}

function AssistantThinkingHistoryModal({
  steps,
  onClose
}: {
  steps: AssistantThinkingStep[];
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!steps.length) return null;
  return (
    <div
      className="creaAssistantTableModal open"
      role="dialog"
      aria-hidden="false"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="creaAsstTableDialog creaAsstThinkingHistoryDialog">
        <div className="creaAsstHead">
          <div>
            <strong>Thinking process</strong>
            <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
              {steps.length} step{steps.length === 1 ? "" : "s"}
            </div>
          </div>
          <button type="button" className="btn" onClick={onClose}>
            Close
          </button>
        </div>
        <div className="creaAsstThinkingHistoryBody">
          {steps.map((s, i) => (
            <div
              key={s.id || `${i}-${s.label}`}
              className={"creaAsstThinkingStep" + (s.status === "done" ? " done" : " active")}
            >
              <span className="creaAsstThinkingDot" aria-hidden="true" />
              <span>{s.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function getCtx() {
  try {
    if (typeof window.__CREA_ASSISTANT_CONTEXT__ === "function") {
      return window.__CREA_ASSISTANT_CONTEXT__() || {};
    }
  } catch {
    /* ignore */
  }
  return {
    surface: "dashboard" as const,
    site: getActiveSite(),
    domain: getActiveSite(),
    path: typeof location !== "undefined" ? location.pathname : "",
    hash: typeof location !== "undefined" ? location.hash : "",
    tab: (location.hash || "").replace(/^#/, "") || "home"
  };
}

function runUiActions(actions: unknown[], setActiveTab: (tab: DashboardTab) => void): void {
  if (!actions.length) return;
  try {
    if (typeof window.__CREA_ASSISTANT_EXECUTE__ === "function") {
      window.__CREA_ASSISTANT_EXECUTE__(actions);
      return;
    }
  } catch {
    /* fallback */
  }
  runAssistantActionsFromChat(actions, setActiveTab);
}

function toPendingMutations(list: AssistantMutationConfirmation[]): AssistantPendingMutation[] {
  return list
    .filter((m) => m && String(m.tool || "").trim())
    .map((m, i) => ({
      id: String(m.id || "").trim() || `mut_${Date.now().toString(36)}_${i}`,
      tool: String(m.tool || "").trim(),
      args: (m.args && typeof m.args === "object" ? m.args : {}) as Record<string, unknown>,
      label: String(m.label || `Allow ${m.tool}?`),
      status: "pending" as const
    }));
}

/** Backend sometimes asks to confirm in prose without pendingMutations — detect and nudge the UI. */
function replyImpliesMissingConfirmation(reply: string, hasPending: boolean): boolean {
  if (hasPending) return false;
  return /\b(please confirm|confirm this action|confirm to proceed|click confirm|use the buttons below)\b/i.test(
    String(reply || "")
  );
}

/** Remove lines that tell users to use a separate dashboard/browser dialog. */
function polishReplyForInlineConfirm(reply: string, hasPending: boolean): string {
  let text = String(reply || "").trim();
  if (!hasPending) return text;
  text = text
    .replace(/please confirm[^\n]*dashboard dialog[^\n]*/gi, "")
    .replace(/confirm (?:the )?(?:action )?in the dashboard dialog[^\n]*/gi, "")
    .replace(/let me know if you need any further assistance[.!]?\s*$/i, "")
    .trim();
  if (!text) {
    return "This action needs your approval. Use the buttons below to confirm or cancel.";
  }
  if (!/buttons below/i.test(text)) {
    text += "\n\nUse the buttons below to confirm or cancel.";
  }
  return text;
}

function actionsAfterMutation(tool: string, result: unknown): unknown[] {
  const r = result && typeof result === "object" ? (result as Record<string, unknown>) : null;
  const taskPayload = r?.task && typeof r.task === "object" ? (r.task as AssistantTask) : null;
  const fromTask =
    taskPayload?.status === "completed" && taskPayload.result && typeof taskPayload.result === "object"
      ? taskPayload.result
      : null;
  const inner =
    fromTask ||
    (r?.result && typeof r.result === "object" ? (r.result as Record<string, unknown>) : r);
  if (!inner || (inner.ok === false && !inner.targetPath)) return [];
  if (tool === "create_page_from_cache" && inner.targetPath) {
    return [
      { type: "set_tab", tab: "site-structure" },
      { type: "open_page_stats", path: String(inner.targetPath) }
    ];
  }
  if (tool === "patch_cached_page") {
    const r = inner as Record<string, unknown>;
    const p = String(r.path || (r.result as Record<string, unknown> | undefined)?.path || "");
    if (p) {
      return [
        { type: "set_tab", tab: "site-structure" },
        { type: "open_page_stats", path: p }
      ];
    }
  }
  if (tool === "deploy_page" || tool === "deploy_site") {
    return [{ type: "set_tab", tab: "deploy" }];
  }
  if (tool === "purge_cache") {
    return [{ type: "set_tab", tab: "cache" }];
  }
  return [];
}

type MutationOutcome = {
  ok: boolean;
  error?: string;
  result?: unknown;
  async?: boolean;
  taskId?: string;
  task?: AssistantTask;
};

async function executeMutation(
  m: AssistantPendingMutation,
  context: Record<string, unknown>
): Promise<MutationOutcome> {
  const data = await dashFetch<{
    ok?: boolean;
    error?: string;
    result?: unknown;
    async?: boolean;
    taskId?: string;
    task?: AssistantTask;
  }>("/dashboard/api/assistant/confirm", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      mutationId: m.id,
      tool: m.tool,
      args: m.args,
      context
    })
  });
  if (!data?.ok) {
    return { ok: false, error: String(data?.error || "unknown"), result: data?.result };
  }
  if (data.async && data.task) {
    return { ok: true, async: true, taskId: data.taskId || data.task.id, task: data.task, result: data };
  }
  return { ok: true, result: data };
}

async function waitForAssistantTask(
  refreshTask: (taskId: string) => Promise<AssistantTask | null>,
  taskId: string,
  upsertTask: (task: AssistantTask) => void
): Promise<AssistantTask | null> {
  const deadline = Date.now() + 600000;
  while (Date.now() < deadline) {
    await new Promise((resolve) => window.setTimeout(resolve, 900));
    const latest = await refreshTask(taskId);
    if (latest) upsertTask(latest);
    if (latest && latest.status !== "running") return latest;
  }
  return refreshTask(taskId);
}

export default function AssistantChat() {
  const dash = useOptionalDashboard();
  const assistantTasks = useOptionalAssistantTasks();
  const setActiveTab = useCallback(
    (tab: DashboardTab) => {
      if (dash) dash.setActiveTab(tab);
      else {
        try {
          location.hash = `#${tab}`;
        } catch {
          /* ignore */
        }
      }
    },
    [dash]
  );
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [mutationBusy, setMutationBusy] = useState(false);
  const [messages, setMessages] = useState<AssistantMessage[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [tableModal, setTableModal] = useState<AssistantTableSpec | null>(null);
  const [contentSearchModal, setContentSearchModal] = useState<PageContentSearchSpec | null>(null);
  const [thinkingHistoryModal, setThinkingHistoryModal] = useState<AssistantThinkingStep[] | null>(null);
  const msgsRef = useRef<HTMLDivElement>(null);
  const siteDomain = String(dash?.activeSite || getActiveSite() || "");
  const userEmail = String(dash?.user?.email || "").trim().toLowerCase();
  const ctxRef = useRef<Record<string, unknown>>({});
  const chatCleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (open && siteDomain) connectDashSocket(siteDomain);
  }, [open, siteDomain]);

  useEffect(() => {
    if (!open || !siteDomain || !userEmail) {
      if (!open) return;
      setMessages([]);
      return;
    }
    let cancelled = false;
    setHistoryLoading(true);
    void fetchAssistantHistory(siteDomain)
      .then((hist) => {
        if (!cancelled) setMessages(clearStalePendingMutations(hist));
      })
      .catch(() => {
        if (!cancelled) setMessages([]);
      })
      .finally(() => {
        if (!cancelled) setHistoryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, siteDomain, userEmail]);

  useEffect(() => {
    return () => {
      try {
        chatCleanupRef.current?.();
      } catch {
        /* ignore */
      }
      chatCleanupRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (msgsRef.current) msgsRef.current.scrollTop = msgsRef.current.scrollHeight;
  }, [messages, open]);

  const patchMutation = useCallback(
    (messageId: string, mutationId: string, patch: Partial<AssistantPendingMutation>) => {
      setMessages((prev) => {
        const next = prev.map((m) => {
          if (m.id !== messageId || !m.pendingMutations) return m;
          return {
            ...m,
            pendingMutations: m.pendingMutations.map((pm) =>
              pm.id === mutationId ? { ...pm, ...patch } : pm
            )
          };
        });
        return next;
      });
    },
    []
  );

  const runMutationConfirm = useCallback(
    async (messageId: string, mutation: AssistantPendingMutation) => {
      if (mutation.status !== "pending") return;

      patchMutation(messageId, mutation.id, { status: "running" });
      try {
        const out = await executeMutation(mutation, ctxRef.current);
        if (out.ok && out.async && out.task && assistantTasks) {
          assistantTasks.openTaskModal(out.task);
          const finished = await waitForAssistantTask(
            assistantTasks.refreshTask,
            out.task.id,
            assistantTasks.upsertTask
          );
          if (finished?.status === "completed") {
            patchMutation(messageId, mutation.id, { status: "confirmed" });
            const followUp = actionsAfterMutation(mutation.tool, { task: finished });
            if (followUp.length) runUiActions(followUp, setActiveTab);
          } else {
            patchMutation(messageId, mutation.id, {
              status: "failed",
              error: finished?.error || "Task did not complete"
            });
          }
        } else if (out.ok) {
          patchMutation(messageId, mutation.id, { status: "confirmed" });
          const followUp = actionsAfterMutation(mutation.tool, out.result);
          if (followUp.length) runUiActions(followUp, setActiveTab);
        } else {
          patchMutation(messageId, mutation.id, {
            status: "failed",
            error: out.error || "failed"
          });
        }
      } catch (e) {
        patchMutation(messageId, mutation.id, {
          status: "failed",
          error: String(e instanceof Error ? e.message : e)
        });
      }
    },
    [patchMutation, setActiveTab, assistantTasks]
  );

  const handleMutationConfirm = useCallback(
    async (messageId: string, mutationId: string) => {
      const msg = messages.find((m) => m.id === messageId);
      const mutation = msg?.pendingMutations?.find((pm) => pm.id === mutationId);
      if (!mutation || mutation.status !== "pending") return;

      setMutationBusy(true);
      try {
        await runMutationConfirm(messageId, mutation);
      } finally {
        setMutationBusy(false);
      }
    },
    [messages, runMutationConfirm]
  );

  const handleMutationConfirmAll = useCallback(
    async (messageId: string) => {
      const msg = messages.find((m) => m.id === messageId);
      const pending =
        msg?.pendingMutations?.filter((pm) => pm.status === "pending") ?? [];
      if (pending.length < 2) return;

      setMutationBusy(true);
      try {
        for (const mutation of pending) {
          await runMutationConfirm(messageId, mutation);
        }
      } finally {
        setMutationBusy(false);
      }
    },
    [messages, runMutationConfirm]
  );

  const handleMutationDecline = useCallback(
    (messageId: string, mutationId: string) => {
      patchMutation(messageId, mutationId, { status: "declined" });
    },
    [patchMutation]
  );

  const sendChat = useCallback(async () => {
    const text = input.trim();
    if (!text || sending || mutationBusy || !siteDomain || !userEmail) return;
    setInput("");
    setSending(true);
    try {
      chatCleanupRef.current?.();
    } catch {
      /* ignore */
    }
    const userMsg: AssistantMessage = { id: newMessageId(), role: "user", content: text };
    const nextHistory = clearStalePendingMutations([
      ...messages.filter((m) => !m.isStreaming && m.content !== "…"),
      userMsg
    ]);
    const assistantMsgId = newMessageId();
    const requestId = `asst_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
    setMessages([
      ...nextHistory,
      {
        id: assistantMsgId,
        role: "assistant",
        content: "",
        thinkingSteps: [{ id: "boot", label: "Starting…", status: "active" }],
        isStreaming: true
      }
    ]);

    const ctx = getCtx();
    ctxRef.current = ctx as Record<string, unknown>;

    const finishAssistant = (payload: {
      ok?: boolean;
      reply?: string;
      error?: string;
      actions?: unknown[];
      pendingMutations?: AssistantMutationConfirmation[];
      thinkingSteps?: AssistantThinkingStep[];
    }) => {
      const pendingRaw = Array.isArray(payload.pendingMutations) ? payload.pendingMutations : [];
      let pendingMutations = pendingRaw.length ? toPendingMutations(pendingRaw) : undefined;
      let reply = polishReplyForInlineConfirm(String(payload?.reply || "").trim(), Boolean(pendingMutations?.length));

      if (replyImpliesMissingConfirmation(reply, Boolean(pendingMutations?.length))) {
        reply +=
          "\n\n**No confirmation buttons were queued.** Ask me again (e.g. “apply that change now”) so I call the site tool and show Confirm/Cancel.";
      }
      if (!payload?.ok && !reply) {
        reply = String(payload?.error || "Request failed");
      }

      const actions = Array.isArray(payload.actions) ? payload.actions : [];
      const hydrated = hydrateAssistantMessage(reply, tablesFromActions(actions));
      const contentSearches = contentSearchesFromActions(actions);
      const steps = Array.isArray(payload.thinkingSteps)
        ? payload.thinkingSteps.map((s) => ({ ...s, status: "done" as const }))
        : undefined;
      const content =
        hydrated.content ||
        (hydrated.tables.length || contentSearches.length ? "" : "(empty)");

      setMessages((prev) => {
        const base = prev.filter((m) => m.id !== assistantMsgId);
        const assistantMsg: AssistantMessage = {
          id: assistantMsgId,
          role: "assistant",
          content: content,
          ...(steps?.length ? { thinkingSteps: steps } : {}),
          ...(hydrated.tables.length ? { tables: hydrated.tables } : {}),
          ...(contentSearches.length ? { contentSearches } : {}),
          ...(pendingMutations?.length ? { pendingMutations } : {})
        };
        return [...base, assistantMsg];
      });

      if (contentSearches[0]) setContentSearchModal(contentSearches[0]);
      if (actions.length && !pendingMutations?.length) runUiActions(actions, setActiveTab);
    };

    chatCleanupRef.current = sendAssistantChat({
      domain: siteDomain,
      requestId,
      messages: nextHistory,
      context: ctx as Record<string, unknown>,
      handlers: {
        onProgress: (step) => {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantMsgId
                ? { ...m, thinkingSteps: mergeThinkingStep(m.thinkingSteps, step) }
                : m
            )
          );
        },
        onDelta: (chunk) => {
          setMessages((prev) =>
            prev.map((m) => (m.id === assistantMsgId ? { ...m, content: `${m.content || ""}${chunk}` } : m))
          );
        },
        onDone: (payload) => {
          setSending(false);
          finishAssistant({
            ok: payload.ok,
            reply: payload.reply,
            error: payload.error,
            actions: payload.actions,
            pendingMutations: payload.pendingMutations as AssistantMutationConfirmation[] | undefined,
            thinkingSteps: payload.thinkingSteps
          });
        },
        onError: (err) => {
          setSending(false);
          finishAssistant({ ok: false, error: err, reply: `Error: ${err}` });
        }
      }
    });
  }, [input, messages, sending, mutationBusy, setActiveTab, siteDomain, userEmail]);

  const hasPendingMutations = messages.some((m) =>
    m.pendingMutations?.some((pm) => pm.status === "pending" || pm.status === "running")
  );

  return (
    <>
      <button
        type="button"
        className="creaAssistantFab"
        title={BRAND_ASSISTANT}
        aria-label={`Open ${BRAND_ASSISTANT}`}
        onClick={() => setOpen(true)}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="M12 3c5 0 9 3.6 9 8 0 2.4-1.2 4.6-3.1 6.1L17 21l-3.5-2.1C12.8 19 12.4 19 12 19c-5 0-9-3.6-9-8s4-8 9-8Z" />
        </svg>
      </button>

      <div className={"creaAssistantPanel" + (open ? " open" : "")} aria-hidden={open ? "false" : "true"}>
        <div className="creaAsstHead">
          <strong>{BRAND_ASSISTANT}</strong>
          <button type="button" className="btn" style={{ padding: "6px 10px" }} onClick={() => setOpen(false)}>
            Close
          </button>
        </div>
        <div className="creaAsstMsgs" ref={msgsRef}>
          {historyLoading ? (
            <div className="creaAsstMsg bot muted">Loading conversation…</div>
          ) : messages.length === 0 ? (
            <div className="creaAsstMsg bot muted">
              Ask me to open tabs, create translated pages from cached HTML, deploy, or find leads. I will ask
              before changing anything on your site.
            </div>
          ) : (
            messages.map((m) => {
              const hydrated =
                m.role === "assistant" && !m.isStreaming
                  ? hydrateAssistantMessage(m.content, m.tables)
                  : null;
              const displayContent = hydrated ? hydrated.content : m.content;
              const displayTables = hydrated?.tables || [];
              return (
              <div key={m.id || m.content.slice(0, 32)} className={"creaAsstMsg " + (m.role === "user" ? "user" : "bot")}>
                {m.role === "user" ? (
                  m.content
                ) : m.isStreaming ? (
                  <AssistantStreamingBubble message={m} linkify={linkify} />
                ) : (
                  <>
                    {m.thinkingSteps && m.thinkingSteps.length > 0 ? (
                      <button
                        type="button"
                        className="creaAsstThinkingHistoryBtn"
                        onClick={() => setThinkingHistoryModal(m.thinkingSteps || [])}
                      >
                        View thinking process
                      </button>
                    ) : null}
                    {displayContent.trim() ? (
                      <div
                        className="creaAsstMsgBody"
                        dangerouslySetInnerHTML={{ __html: linkify(displayContent) }}
                      />
                    ) : null}
                    {m.pendingMutations?.length ? (
                      <AssistantMutationPrompt
                        mutations={m.pendingMutations}
                        disabled={mutationBusy}
                        onConfirm={(id) => void handleMutationConfirm(m.id || "", id)}
                        onDecline={(id) => handleMutationDecline(m.id || "", id)}
                        onConfirmAll={() => void handleMutationConfirmAll(m.id || "")}
                      />
                    ) : null}
                    {displayTables.length > 0 ? (
                      <div className="creaAsstTableBtns">
                        {displayTables.map((t, ti) => (
                          <button
                            key={t.tableId || String(ti)}
                            type="button"
                            className="creaAsstOpenTableBtn"
                            onClick={() => setTableModal(t)}
                          >
                            {t.title || "View table"}
                          </button>
                        ))}
                      </div>
                    ) : null}
                    {Array.isArray(m.contentSearches) && m.contentSearches.length > 0 ? (
                      <div className="creaAsstTableBtns">
                        {(m.contentSearches as PageContentSearchSpec[]).map((s, si) => (
                          <button
                            key={si}
                            type="button"
                            className="creaAsstOpenTableBtn"
                            onClick={() => setContentSearchModal(s)}
                          >
                            {pageContentSearchLabel(s)}
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </>
                )}
              </div>
              );
            })
          )}
        </div>
        <div className="creaAsstFoot">
          <textarea
            rows={2}
            value={input}
            placeholder={hasPendingMutations ? "Confirm or cancel the action above…" : "Ask anything…"}
            disabled={mutationBusy}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                void sendChat();
              }
            }}
          />
          <button
            type="button"
            className="creaAsstSend"
            disabled={sending || mutationBusy}
            onClick={() => void sendChat()}
          >
            Send
          </button>
        </div>
      </div>

      {thinkingHistoryModal?.length ? (
        <AssistantThinkingHistoryModal
          steps={thinkingHistoryModal}
          onClose={() => setThinkingHistoryModal(null)}
        />
      ) : null}

      {siteDomain ? (
        <PageContentSearchModal
          open={Boolean(contentSearchModal)}
          domain={siteDomain}
          spec={contentSearchModal}
          initialQuery={contentSearchModal?.payload?.query || ""}
          onClose={() => setContentSearchModal(null)}
        />
      ) : null}

      {tableModal ? (
        <div
          className="creaAssistantTableModal open"
          role="dialog"
          aria-hidden="false"
          onClick={(e) => {
            if (e.target === e.currentTarget) setTableModal(null);
          }}
        >
          <div className="creaAsstTableDialog">
            <div className="creaAsstHead">
              <div>
                <strong>{tableModal.title || "Data"}</strong>
                {tableModal.subtitle ? (
                  <div className="muted" style={{ fontSize: 11 }}>
                    {tableModal.subtitle}
                  </div>
                ) : null}
              </div>
              <button type="button" className="btn" onClick={() => setTableModal(null)}>
                Close
              </button>
            </div>
            <div className="creaAsstTableBody">
              <AssistantTable spec={tableModal} />
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

function AssistantTable({ spec }: { spec: AssistantTableSpec }) {
  let columns = spec.columns || [];
  const rows = spec.rows || [];
  if (!columns.length && rows.length && rows[0]) {
    columns = Object.keys(rows[0]).map((k) => ({ key: k, label: k }));
  }
  if (!rows.length) return <div className="muted">No rows.</div>;
  return (
    <table>
      <thead>
        <tr>{columns.map((c) => <th key={c.key}>{c.label || c.key}</th>)}</tr>
      </thead>
      <tbody>
        {rows.map((row, ri) => (
          <tr key={ri}>
            {columns.map((c) => (
              <td key={c.key} className="mono">
                {String(row[c.key] ?? "")}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
